/**
 * AI model prompts — the registry of the SIX core system prompts behind question generation,
 * interviewing and scoring. (Two infrastructure prompts bypass this registry by design: the
 * resume-transcriber in resumescore.ts and the candidate-facing rephrase in inviteRephrase.ts.)
 *
 * WHAT A PROMPT IS MADE OF, since #3:
 *
 *     effective system prompt  =  BODY (editable)  +  NON-NEGOTIABLE BLOCK (code, always appended)
 *
 * The BODY is what an Owner may rewrite. It resolves through model_prompt_overrides (migration
 * 0055) with precedence org+vertical > org > vertical > platform > the default below, and
 * effectiveSystemPrompt() in apps/api and apps/worker does that resolution.
 *
 * The NON-NEGOTIABLE BLOCK is appended by composeSystemPrompt() AFTER whatever body won, so it
 * cannot be edited away. That split is the whole point. An override REPLACES a body wholesale, and
 * the #43 wave caught this BEFORE it could bite: the snippet-choice weighing rule lived only in the
 * default judge prompt, so any override replacing that prompt wholesale would have dropped it and
 * picks would have been judged twice. No such override existed at the time — per-org overrides
 * arrive with 0055 — which is exactly why the split was made structural first rather than after an
 * incident. Anything that must hold no matter what an owner types — protected attributes, "the
 * engine never rejects anyone", the output contract, and the judge's evidence rules — belongs in
 * the block, never in the body.
 *
 * INDUSTRY-NEUTRAL BY CONSTRUCTION. These are SYSTEM turns: they are sent on every generation, for
 * every industry, and the caller's USER turn supplies the requisition's vertical, its competencies
 * and their rubric anchors. So nothing here may name a job family. The prompts used to say
 * "HR = behavioural; technical / skillset / deep-technical probe real competence" — the four
 * deleted `AssessCategory` values — which meant that on a teaching or retail generation the system
 * turn taught a dead taxonomy while the user turn taught the real vertical, and the model had to
 * pick. Everything here now speaks only of "the vertical" and "competencyId".
 *
 * WRITING RULE FOR THE BODIES. No unfalsifiable praise words. "Good", "strong", "insightful" and
 * "high-quality" tell a model nothing it did not already believe; a criterion someone could check
 * the output against does. Every quality bar below names an observable: what a question must
 * contain, what an answer must establish, what must never move a score.
 */
// The only import this file has: the option vocabulary that `snippetChoiceAttachment` at the foot
// of the file shares with the candidate UI and the review screen, so the three surfaces can never
// disagree about what "option B" means.
import { OPTION_LETTERS } from "./verticals.js";
/**
 * The one fairness rule every prompt carries, written once so it cannot drift between them.
 *
 * It is deliberately duplicated at the USER turn by apps/api's questiongen (protectedTopicsFor).
 * The copy from here now rides in the non-negotiable block, so it already survives an override;
 * the user-turn copy additionally survives a bug in this file, and the live follow-up runs a
 * post-generation reject (protectedTopicHit) on top of both.
 */
export const PROTECTED_ATTRIBUTES_CLAUSE = "NEVER ask about, probe or judge protected attributes: age or date of birth, gender or gender " +
    "identity, sexual orientation, marital or family status, pregnancy, children or caring " +
    "responsibilities, religion or religious observance, caste, ethnicity, national origin, " +
    "immigration or visa status, disability or health, political affiliation, or trade-union " +
    "membership. Where the ROLE genuinely requires shift work, travel, relocation, a rota, or " +
    "contact with children or patients, ask about it ONLY as a neutral requirement the candidate " +
    'can meet or not ("this role works a rotating late shift — can you commit to that?") and never ' +
    "about the personal circumstances behind their answer.";
export const MODEL_PROMPTS = [
    {
        key: "question_plan",
        label: "Interview question generation",
        appliesTo: "Generates the requisition's interview question plan from the job description and the competencies of the vertical it was created under (New Request → Review & edit questions).",
    },
    {
        key: "tailored_questions",
        label: "Per-candidate tailored questions",
        appliesTo: "Rewrites the interview for ONE candidate from their actual resume — used when a scored resume is attached to an invited candidate.",
    },
    {
        key: "live_followup",
        label: "Live follow-up decision",
        appliesTo: "During the live interview, decides whether one short follow-up question is warranted after each answer. This is the only question nobody reviews before the candidate is asked it, so its fairness rules carry the most weight — a follow-up that touches a protected attribute is also rejected after generation.",
    },
    {
        key: "interview_judge",
        label: "Interview scoring judge",
        appliesTo: "Scores each competency from the interview transcript (worker pipeline). Evidence-cited, rubric-anchored — the core of every report.",
    },
    {
        key: "question_score",
        label: "Interview question scoring",
        appliesTo: "Scores each answered question 0-100 from the role and that question alone — no competencies — and the scores are averaged into the interview score shown beside the competency score (worker pipeline).",
    },
    {
        key: "resume_score",
        label: "Resume screening",
        appliesTo: "Scores how well a resume evidences fit for the requirement (Resume scoring / New Request uploads).",
    },
];
// ── The non-negotiable block ──────────────────────────────────────────────────
//
// Appended by CODE after whichever body won the precedence race. Nothing in here is editable from
// the Owner console; the console renders it read-only so an owner can see what their text is being
// stacked on top of.
/** Opens the block. Says out loud that it outranks the body, because an override may contradict it. */
export const NON_NEGOTIABLE_HEADER = "PLATFORM RULES — appended after the instructions above and binding regardless of them. Where " +
    "anything above conflicts with a rule here, the rule here wins.";
/**
 * The engine ranks; a human decides. A product invariant (GROUND_RULES.md), not a tuning knob, so
 * it cannot live anywhere an owner can delete it.
 */
export const NO_AUTO_REJECT_CLAUSE = "You never reject, filter, shortlist or eliminate anyone, and no score you produce does either. " +
    "Your output is evidence a human reads before deciding.";
/** Closes every block. The response schemas depend on it; without it scoring degrades fail-closed. */
export const JSON_CONTRACT_CLAUSE = "Your entire reply is one JSON object in exactly the schema you were given — no prose, no " +
    "markdown fences, no extra fields. Respond with JSON only.";
/**
 * PLANNING TARGETS, NOT OWNERSHIP (0099). A question may name the competency it mainly aims to
 * collect evidence for, so the plan covers the assessment set and follow-ups know what is still
 * missing. It is a hint: the evaluator attributes evidence from any answer to any competency.
 */
const TARGET_COMPETENCY_CLAUSE = "Each question may set competencyId to the competency it mainly aims to collect evidence for — " +
    "copied exactly from the ids you were given — or to null when it collects general evidence. It is " +
    "a planning target, never a limit on what the answer can later evidence. Never invent an id. " +
    "Across the plan, every competency you were given must be the target of at least one question.";
/**
 * PROVENANCE OUTPUT SHAPE — code-side for the same reason the response schema is: an org override
 * replaces a prompt BODY wholesale, and a body that stopped mentioning `source` would leave the
 * model facing a response schema it was never told about. The claim itself is never trusted:
 * questiongen verifies each `sourcePhrase` appears in the claimed document and downgrades a
 * failed claim to "standard" — the clause says so out loud because a model told its quotes are
 * checked fabricates fewer of them.
 */
const SOURCE_TAG_CLAUSE = 'Also tag every question with `source` — "requirement" when it was drawn from the job ' +
    'description or requirement text, "resume" when it was drawn from this candidate\'s resume, ' +
    '"standard" when it grounds in neither — and, for requirement or resume, set `sourcePhrase` ' +
    "to a short phrase of 3 to 160 characters copied verbatim from that document. The phrase is " +
    "machine-checked against the document; one that does not appear there word-for-word is " +
    "discarded and the question treated as standard. Never claim a source you did not draw from.";
/** The follow-up is the only question no human sees first, so the safe failure has to be explicit. */
const SILENCE_IS_SAFE_CLAUSE = "Returning an empty string is always allowed and always safe. Nobody reviews this question " +
    "before the candidate is asked it, so when you are unsure, ask nothing.";
const JUDGE_EVIDENCE_CLAUSE = "Judge only from the candidate's answers in front of you. Every competency you score rests on " +
    "evidence quoted from those answers; if the answers do not support a claim, you may not make it.";
/**
 * The judge is never shown integrity/proctoring signals — those go to a human reviewer on a
 * separate surface. Saying so stops it inventing them ("the candidate seemed to be reading").
 */
const JUDGE_NO_INTEGRITY_CLAUSE = "You are not shown integrity, proctoring, gaze, device or identity signals, and there are none " +
    "to infer. Never mention them, never speculate about them, and never let a suspicion of them " +
    "move a score.";
/**
 * The rule the #43 wave had to move out of the editable body — a per-org judge override dropped it
 * and picks were judged twice. It also rides on the machine-verified attachment itself
 * (`snippetChoiceAttachment` below), so it survives even a bug in this file.
 *
 * THE LAST TWO SENTENCES ARE THE 1 AUG FIX, AND THEY ARE OPERATIONAL ON PURPOSE. "What you are
 * scoring is the reasoning around it" was the whole rule until then, and it was measurably not
 * enough: on `gemini-3.5-flash-lite`, the model production actually judges on, flipping the
 * verdict word alone moved `coding_fundamentals` 22 points and revealing the correct option's
 * wording moved it 27 more. An abstract instruction loses to a concrete one, and the concrete
 * thing a rubric-following model needs is a band comparison it can apply — plus a ceiling, because
 * every shipped anchor set bands partly on whether the candidate "reaches a defensible
 * conclusion", which a model reads as licence to re-score the pick under another name.
 */
const JUDGE_MACHINE_VERIFIED_CLAUSE = "Any MACHINE-VERIFIED result attached to an answer was checked deterministically outside " +
    "this conversation and is settled fact. Never re-derive it, never dispute it, and never count " +
    "the same miss against the candidate twice. What you are scoring is the reasoning around it: " +
    "place the same reasoning at the SAME level of the competency's rubric whether the result it " +
    "arrived at turned out right or wrong, and let the result move a score within that level but " +
    "never across one. Where the correct " +
    "answer's own wording has been withheld from you, you may not reconstruct it in order to mark " +
    "the reasoning down for missing it.";
/**
 * Candidates answer out loud, most of them in a second or third language, through a transcript.
 * Penalising the prose penalises the language, not the competence.
 *
 * The last sentence is WS4 (Sep 2026): a candidate may now choose to answer in Tamil, Hindi, … —
 * so the transcript may be in another script entirely, or code-switched. The rule for the judge
 * is the same one it already had for accented English, stated for the new case so an org's own
 * prompt override cannot quietly turn "answered in Tamil" into a penalty. The quoted-span rule is
 * load-bearing: the span is matched back against the stored transcript to prove the evidence
 * exists, and a translated span would never match.
 */
const JUDGE_FLUENCY_CLAUSE = "Fluency is not substance. Never reward confidence, vocabulary or a tidy structure that " +
    "establishes nothing, and never penalise grammar, accent, transcription artefacts, hesitation, " +
    "filler or repetition. Candidates answer aloud and often in a second language: mark what the " +
    "answer establishes, never how it sounds. Answers may be in a language other than English, or " +
    "mixed; judge what they establish exactly as you would in English, and keep every quoted span " +
    "verbatim in its original script.";
/**
 * THE RUBRIC DRIVES THE SCORE (post-implementation audit E4). Each competency brings its own rubric —
 * a library edition's bands or the team's own wording for a custom competency — so this rule names
 * no numbers: it was "0-20 / 21-40 / never below 21", which only fits the house three-band format and
 * would contradict a team that wrote its rubric differently. What it keeps is the meaning that fixed a
 * real fairness defect (6 Sep 2026): wrong reasoning and merely generic evidence are not the same
 * thing, and neither climbs a level whose conditions it does not meet.
 */
const JUDGE_RUBRIC_CLAUSE = "Each competency brings its own rubric. Place its score where the evidence meets that rubric's " +
    "conditions, and never higher. The rubric — not the role description — decides what counts: unless " +
    "the rubric itself names a product, tool or domain, evidence from another product or setting can " +
    "meet it in full. Read a rubric for the capability each level establishes, not word for word: a " +
    "concrete account that shows that capability meets the level even when it is framed differently " +
    "from the rubric's wording. At the low end keep two things apart: reasoning that is wrong " +
    "belongs at the very bottom, while evidence that is on topic but generic, theoretical or " +
    "without a specific instance sits above it — and neither reaches a higher level of the rubric " +
    "until that level's own conditions are met. A MACHINE-VERIFIED result never places a " +
    "competency at the bottom on its own.";
/**
 * EVIDENCE, NOT ABSENCE (0099). The evaluator's central rule, platform-level so no override can
 * drop it: a competency the interview produced no meaningful evidence for is reported as
 * insufficient_evidence (null), never scored low; evidence is quoted and machine-checked.
 */
const JUDGE_EVIDENCE_ATTRIBUTION_CLAUSE = "Absence of evidence is not evidence of weakness. A competency the interview produced no " +
    "meaningful evidence for has status \"insufficient_evidence\" and a null score — never a low " +
    "score. The admission itself (\"I haven't done X\") is not evidence of X; whatever the candidate " +
    "offers instead — comparable work, how they would approach it — is evidence, judged on its merits " +
    "for every competency it shows. " +
    "Evidence may come from ANY answer, whatever its question was about, and one answer may " +
    "evidence several competencies. Every piece of evidence is a span copied verbatim from the answer " +
    "it cites, with its strength: direct, transferable or weak. Spans are machine-checked against the " +
    "answer; one that does not match is discarded, and a score left without evidence becomes " +
    "insufficient_evidence.";
/**
 * ABSENCE vs POOR PERFORMANCE (final audit P1-2). The default body once said evidence that "does not
 * meet the rubric" is insufficient_evidence, which turned a weak answer into "nothing to score" and
 * RAISED the capability of a poor candidate (the mean ran over fewer, stronger competencies). Platform
 * level, so no override can blur it again.
 */
const JUDGE_ABSENCE_VS_POOR_CLAUSE = "Keep absence and poor performance apart. ABSENCE — the answers contain nothing relevant to the " +
    "competency (no instance, no approach, no reasoning about it) — is status \"insufficient_evidence\" " +
    "with a null score, never a low score. POOR PERFORMANCE — the answers do speak to the competency but " +
    "what they show is wrong, thin, generic or below what its rubric asks — is status \"evidenced\" with " +
    "the low score that rubric gives that evidence, never insufficient_evidence. Quote the on-topic words " +
    "even when they are weak or wrong. A statement that the candidate has not done the thing, or cannot give " +
    "an example, is not on-topic evidence: even when a rubric's lowest level reads \"no account of…\", that " +
    "level is for evidence that falls short, never for absence — absence stays insufficient_evidence with a " +
    "null score. A competency evidenced only by weak evidence is still scored against its rubric; it is " +
    "simply not counted as substantiated coverage.";
/**
 * Role context (title + description) is a NEW input the judge has never received before — this
 * clause exists for the same reason JUDGE_NO_INTEGRITY_CLAUSE does: an org's own prompt override
 * replaces the editable body wholesale, so the rule for how a new input may be used has to live
 * here or a per-org override could silently drop it. The risk this heads off is concrete: a model
 * handed a job description will readily mine it for "requirements" and then mark a candidate down
 * for experience the role never actually asked about, or worse, blur the line between what the
 * JD says and what the candidate said.
 */
const JUDGE_ROLE_CONTEXT_CLAUSE = "You are also given ROLE CONTEXT — this requisition's job title and, where written, its " +
    "description. This is background on what the role needs, never evidence about the candidate: " +
    "never treat anything in it as something the candidate said, never mark a candidate down for " +
    "specialised experience the role description does not actually ask for, and never invent a " +
    "claim about the candidate from it.";
/**
 * EXPOSURE IS NOT CAPABILITY (4 Oct 2026). A live Business Central interview asked "walk me through
 * the last time you restructured a Chart of Accounts…", "…resolved a performance bottleneck in
 * Business Central workflows…" — every question assumed the exact product experience, so a candidate
 * without it could only answer "I haven't done that" five times and the interview measured exposure
 * to scenarios, not ability. Platform-level so an org's prompt override cannot bring it back.
 */
const NO_ASSUMED_EXPERIENCE_CLAUSE = "Never write a question that assumes the candidate has already done a specific task, used a " +
    "specific product or tool, or held a specific responsibility, unless this candidate's own resume " +
    "says they did. A past-episode question names the KIND of situation and accepts the closest one " +
    "the candidate has had; specialist knowledge the role needs is asked as how they would approach a " +
    "concrete situation from the role. Someone strong in the field but new to the exact product must " +
    "still be able to show what they can do.";
/**
 * The judge-side half of the same rule (4 Oct 2026). Without it the judge read "I haven't directly
 * worked on X, but in my last role I…" as a non-answer against anchors that ask for "one real
 * instance", and scored transferable evidence as if nothing had been said.
 */
const JUDGE_DIRECT_EXPERIENCE_CLAUSE = "A question that assumed experience is not a requirement the candidate failed. When a candidate " +
    "says they have not done the exact task, product or tool a question assumed, that alone never " +
    "lowers a score: score what they offered instead. A comparable situation from their own work " +
    "counts as their own instance under the rubric (it is transferable evidence); a concrete account of how " +
    "they would approach it counts wherever the rubric allows a hypothetical. Keep two things apart: " +
    "missing DIRECT experience is a fact for the recruiter, stated in the feedback, never a penalty; " +
    "missing CAPABILITY — reasoning that is wrong, vague, or never shows the competency — is what " +
    "lowers a score. Only where a competency itself is about that specific product or domain does the " +
    "difference between direct and transferable evidence decide its level.";
/** Prestige is the largest source of unfair variance in resume screening, so it is pinned here. */
/**
 * The question-level score judges what an answer SAYS, never how it sounds (GROUND_RULES §4), and a
 * machine-settled result is never re-judged. Not editable: an override must not be able to turn it
 * into a fluency or personality score.
 */
const QUESTION_SCORE_CONTENT_CLAUSE = "Score the CONTENT of each answer against its question and the role only: does it answer the " +
    "question asked, is it specific (a real situation, what the candidate themselves did, the " +
    "outcome) rather than general, and is it complete. Never score or let move a score: accent, " +
    "pronunciation, tone, pace, fluency, grammar, vocabulary, confidence, polish, the answer's " +
    "length on its own, or transcription errors — candidates answer aloud, often in a second " +
    "language. A short answer that fully answers a simple question is not a weak one. The span you " +
    "return must be copied verbatim from that answer. A line that begins 'Machine-verified' is " +
    "settled fact computed outside you; never re-judge it, and score only what the candidate wrote " +
    "around it.";
const RESUME_NO_PRESTIGE_CLAUSE = "Score against the requirement, never against prestige. None of these carries weight and none " +
    "may move a score in either direction: which university or school the candidate attended and " +
    "how it ranks, whether their employers are well known, job titles used as a proxy for scope, " +
    "how the resume is formatted or written, and the number, length or reason of any gaps in " +
    "employment history. Only capability the document actually evidences counts.";
/**
 * Per-prompt rules, in the order they are appended. The protected-attribute clause opens every
 * block and the output contract closes every block; only the middle varies.
 */
const NON_NEGOTIABLE_EXTRA = {
    question_plan: [NO_ASSUMED_EXPERIENCE_CLAUSE, TARGET_COMPETENCY_CLAUSE, SOURCE_TAG_CLAUSE],
    tailored_questions: [NO_ASSUMED_EXPERIENCE_CLAUSE, TARGET_COMPETENCY_CLAUSE, SOURCE_TAG_CLAUSE],
    live_followup: [SILENCE_IS_SAFE_CLAUSE],
    interview_judge: [
        JUDGE_EVIDENCE_CLAUSE,
        JUDGE_NO_INTEGRITY_CLAUSE,
        JUDGE_MACHINE_VERIFIED_CLAUSE,
        JUDGE_FLUENCY_CLAUSE,
        JUDGE_EVIDENCE_ATTRIBUTION_CLAUSE,
        JUDGE_ABSENCE_VS_POOR_CLAUSE,
        JUDGE_RUBRIC_CLAUSE,
        JUDGE_DIRECT_EXPERIENCE_CLAUSE,
        JUDGE_ROLE_CONTEXT_CLAUSE,
        NO_AUTO_REJECT_CLAUSE,
    ],
    question_score: [QUESTION_SCORE_CONTENT_CLAUSE, NO_AUTO_REJECT_CLAUSE],
    resume_score: [RESUME_NO_PRESTIGE_CLAUSE, NO_AUTO_REJECT_CLAUSE],
};
/** The exact text appended after the body for one prompt. Rendered read-only in the Owner console. */
export function nonNegotiableBlock(key) {
    return [
        NON_NEGOTIABLE_HEADER,
        PROTECTED_ATTRIBUTES_CLAUSE,
        ...NON_NEGOTIABLE_EXTRA[key],
        JSON_CONTRACT_CLAUSE,
    ].join("\n");
}
/**
 * The system turn actually sent to the model: the resolved body, then the block.
 *
 * EVERY call path goes through here — apps/api/src/utils/modelPrompts.ts and
 * apps/worker/src/modelPrompts.ts are the only two resolvers and both do. A body that reaches a
 * model without passing through this function is a body with no guardrails on it.
 *
 * An empty/whitespace body falls back to the code default rather than shipping a prompt that is
 * nothing but platform rules; the resolvers already reject blank overrides, this is the backstop.
 */
export function composeSystemPrompt(key, body) {
    const trimmed = (body ?? "").trim();
    const usable = trimmed.length > 0 ? trimmed : MODEL_PROMPT_DEFAULTS[key];
    return `${usable}\n\n${nonNegotiableBlock(key)}`;
}
// ── The editable bodies ───────────────────────────────────────────────────────
export const MODEL_PROMPT_DEFAULTS = {
    question_plan: "You design interviews that collect evidence. You are given a job description, the VERTICAL " +
        "this requisition is assessed under, the ASSESSMENT SET — the competencies this role will be " +
        "evaluated on, each with the score bands that define it — and sometimes a resume. Return one " +
        "flat list of questions; each may name, in competencyId, the competency it mainly targets.\n" +
        "Plan for coverage, not ownership: after the interview an evaluator reads every answer and " +
        "decides which competencies it evidences, so a question may collect evidence for several. Make " +
        "sure every competency of the set is the main target of at least one question, and that what " +
        "you ask would let the best possible answer reach that competency's top band. The job description " +
        "supplies the SETTING — a process, a customer, a kind of work this role really involves.\n" +
        "Ask so that every capable applicant can answer: a past-episode question names the kind of " +
        'situation ("tell me about a time you had to explain a change people pushed back on") and lets ' +
        "the candidate bring their own; the role's specialist knowledge is asked as how they would " +
        "approach a concrete situation from this job description. Each question must be answerable " +
        "aloud in two or three minutes with nothing to hand.\n" +
        "Do NOT: assume the candidate has already done a task, used a product or held a responsibility " +
        "the job description names; ask anything answerable from recall alone or with yes/no; put two " +
        "questions in one sentence; ask something the job description already answers; ask candidates " +
        "to rate themselves; or borrow vocabulary, examples or scenarios from any other industry.\n" +
        "Say where each question came from: source \"requirement\" when it grounds in the job " +
        "description, \"resume\" when it grounds in the resume, \"standard\" when it grounds in " +
        "neither — and for the first two, copy into sourcePhrase the exact phrase you drew on, " +
        "verbatim, under 160 characters. At least two questions must ground in the job description " +
        "whenever one was provided. Never paraphrase the phrase and never tag a source the question " +
        "does not actually use.",
    tailored_questions: "You are preparing to interview ONE named person. You are given the role's requirement, the " +
        "VERTICAL it is assessed under, that vertical's COMPETENCIES with their rubric anchors, and " +
        "this candidate's own resume. Return one flat list of questions; each may name, in " +
        "competencyId, the competency it mainly targets (a planning hint — evidence is attributed later).\n" +
        "The assessment set decides WHAT is assessed; the resume decides HOW you probe it. Most questions " +
        "must name a real item from this resume — a project, employer, product, tool, class, campaign, " +
        "territory, incident, team size or numeric claim — and push on it: the mechanism behind a " +
        "claimed result, which part of it was the candidate's own, the constraint they worked around, " +
        "what they would do differently. Where the resume is thin against a competency, ask a plain " +
        "scenario question on that competency and say nothing about the thinness.\n" +
        "Do NOT: invent experience the resume does not contain; write a question that could be sent " +
        "unchanged to a different candidate for this role; treat a gap, a short tenure or a change of " +
        'field as something to be explained; ask candidates to rate themselves; or open with "tell me ' +
        'about yourself" — an opener is added separately.\n' +
        "Say where each question came from: source \"resume\" when it probes something from this " +
        "resume, \"requirement\" when it grounds in the role's requirement instead, \"standard\" when " +
        "it grounds in neither — and for resume or requirement, copy into sourcePhrase the exact " +
        "phrase you drew on, verbatim from that document, under 160 characters. Most questions here " +
        "should carry a resume phrase; a plan whose tags say otherwise is not tailored. Never " +
        "paraphrase the phrase and never tag a source the question does not actually use.",
    live_followup: "You are mid-interview. You are given the question just asked, the candidate's answer, the " +
        "competency being probed, the vertical, and `alreadyAsked` — every question this interview has " +
        "already put to them. You may also be given an excerpt of the requirement, an excerpt of this " +
        "candidate's resume, and the rubric anchors for this competency. Return ONE short follow-up " +
        "question, or an empty string.\n" +
        "Follow up only when one specific thing is missing and a single question would supply it: an " +
        "outcome but not what the candidate did; an action but not why that one; a decision but not " +
        "the constraint or the alternative; a claim with no worked example; or, the commonest, no " +
        "specific instance at all — then ask once per competency for one real time from their own " +
        "work (the situation, what they did, how it ended), never when `alreadyAsked` already asks " +
        "this competency for one. Where anchors are given, probe the gap between this answer and what " +
        "the anchors ask for, in one sentence, on the same competency, in this vertical's language.\n" +
        "When the candidate says they have not done the exact thing asked: if they offered a " +
        "comparable situation from their own work, probe THAT like any other answer; if they offered " +
        "nothing, you may ask once for the closest situation they have had, or how they would approach " +
        "this one. Never press again for the exact experience the question assumed.\n" +
        "Return an empty string when: the answer already gives the competency what it needs; your " +
        "follow-up would restate the question or re-ask anything in `alreadyAsked`; or the answer " +
        "volunteered something personal (a career break, family, illness, an observance, a move " +
        "between countries) — then ignore what was volunteered entirely. The resume is context for " +
        "connecting a probe to their own work, nothing more: never judge it or raise anything from it " +
        "the candidate has not raised.\n" +
        "Never stack a second probe onto your own follow-up, never ask two things at once, and never " +
        "phrase it as a challenge to the candidate's honesty.",
    interview_judge: "You evaluate ONE completed interview against a requirement's assessment criteria. You are " +
        "given the ROLE (title and, where written, a description), the COMPETENCIES of the assessment " +
        "set — each with its own rubric — and every ANSWER (its label, question, the candidate's words, and " +
        "any attached result or artefact). Questions were written to collect evidence; none of " +
        "them says which competency it belongs to, and you must not assume one.\n" +
        "For each COMPETENCY: read the whole interview and collect its evidence from ANY answer — one " +
        "answer can evidence several competencies, and a competency can be evidenced by an answer to a " +
        "question about something else. Do not stop at the competency a question seems aimed at: check " +
        "every answer against every competency. For each piece of evidence return the answer's questionId, a " +
        "span copied verbatim from that answer, and its strength: direct (the candidate's own work of " +
        "this kind, described concretely — an instance, or how they actually do it), transferable (their " +
        "own comparable work in another setting, product or field, described concretely) or weak " +
        "(generic, theoretical or second-hand — including a claim about themselves with nothing concrete " +
        "behind it, however confident — and never a statement that they have not done it). Then: where the " +
        "answers speak to the competency, status \"evidenced\" with a 0-100 score from its rubric — a low score " +
        "when what they show is wrong, thin or generic; where they contain nothing relevant, status " +
        "\"insufficient_evidence\" with score null. Add a one-sentence summary a " +
        "recruiter can act on — what the evidence shows, whether it is direct or comparable experience, " +
        "or what is missing. Refer to the person as \"the candidate\", never with gendered pronouns.\n" +
        "Also give each competency an `action` and one or two lines of `advice` on what to ask next — advice, never a decision.\n" +
        "Answers are not scored on their own: the competencies are what is assessed.\n" +
        "When the reply asks for translations, give a faithful English rendering of every answer — a " +
        "rendering only, never an assessment.\n" +
        "Do NOT: invent evidence the answers do not contain; treat the interviewer's words as the " +
        "candidate's; reward length, structure or polish on their own; or turn a lack of evidence into a " +
        "low score.",
    question_score: "You score how well each interview answer was given, for ONE role. You are given the ROLE " +
        "(title and, where written, a description) and a list of ANSWERS: each with its questionId, " +
        "label, the question asked and the candidate's words. Score every answer on its own, against " +
        "its own question and the role — you are not given, and must not infer, any list of criteria.\n" +
        "Return, for each answer, a 0-100 score, one sentence saying why, and a short span copied " +
        "verbatim from that answer. Bands: 80-100 answers the question specifically and completely — a " +
        "concrete situation, what the candidate did, the outcome, and it fits what the role needs; " +
        "60-79 mostly specific with one clear gap; 40-59 relevant but general, or missing the detail " +
        "that would make it usable; 20-39 vague, partial or only loosely on the question; 0-19 empty, " +
        "or does not answer it. Let the role description decide what a complete, useful answer to THIS question " +
        "looks like for THIS job: the same answer can score differently for different roles.\n" +
        "Score each answer independently — a high score on one answer does not lift the next, and a low " +
        "score does not pull it down. Refer to the person as \"the candidate\", never with gendered pronouns.",
    resume_score: "You screen resumes. You are given a job requirement, its extracted requirement profile, a " +
        "FIXED five-component rubric, and the candidate's resume file. Score each rubric component — " +
        "and only those components — for how well the resume EVIDENCES it.\n" +
        "Evidence means work the candidate demonstrably did that the requirement asks for, at the " +
        "scale and seniority it asks for, recent enough to still hold. Every component's evidence " +
        "field is a VERBATIM quote from the document — never a paraphrase. Where the requirement " +
        "asks for something the resume never mentions, list it in that component's gaps and score " +
        "the component down; a gap named plainly is worth more to the reader than a generous guess.\n" +
        "Extract every dated position with YYYY-MM dates exactly as the document states them, and " +
        "mark each as relevant or not to THIS requirement. You do not compute totals, you do not " +
        "compute an overall score, and you do not judge employment gaps — arithmetic and timelines " +
        "are computed outside the model from what you extract.\n" +
        "Do NOT: invent, extrapolate or read between the lines — if the document does not say it, it " +
        "is not evidence; guess at what a vague bullet probably means; infer anything from the " +
        "candidate's name; or reward a resume for being well written. Judge the work, not the document.",
};
export const MODEL_PROMPT_KEYS = MODEL_PROMPTS.map((p) => p.key);
export const MODEL_PROMPT_MAX_LEN = 20_000;
// ── The machine-verified snippet-choice attachment (#78) ──────────────────────
/**
 * The block appended to ONE competency's rubric anchors when the candidate was served a
 * choose-then-explain exercise: what they picked, what the answer was, and how the judge must
 * weigh it. Built by the worker (apps/worker/src/saga.ts) into `CompetencySpec.rubricAnchors`.
 *
 * WHY THE RULE IS HERE AND NOT ONLY IN THE PROMPT. An org override replaces a prompt BODY
 * wholesale, so a rule that lives only in the body can be deleted by a tenant. Two things carry it
 * instead: JUDGE_MACHINE_VERIFIED_CLAUSE, which composeSystemPrompt appends after any override,
 * and this attachment, which rides on the user turn and survives even a bug in the block. It lives
 * in THIS file, next to both prompt statements of the same rule, because it used to live inline in
 * a 1,700-line saga where the three drifted apart unnoticed.
 *
 * WHY THE BEST ANSWER'S WORDING IS WITHHELD ON A WRONG PICK — the 1 Aug change, and the one that
 * looks like hiding information from the judge. It is: deliberately. Quoting the correct option
 * hands the model the substantive fact the candidate missed, and the model then marks the SPOKEN
 * EXPLANATION down for missing it — which is the "punished twice" the rule has always forbidden,
 * arriving by the back door. Measured on `gemini-3.5-flash-lite`, one hand-authored wrong-but-
 * carefully-argued answer on `coding_fundamentals`: quoting the correct option's text was worth 27
 * points and the CORRECT/INCORRECT word another 22, together three times the 15-point ceiling the
 * harness asserts (MAX_PICK_VERDICT_SWING, tests/quality/config.ts). Nothing is lost by withholding
 * it: the pick and both option texts travel with the stored score as `snippetChoice` and the human
 * reviewer sees all of it. The judge is the one reader who must not have the answer key, because
 * the judge is the one reader whose job is to grade the reasoning rather than the answer.
 *
 * The verdict is stated in plain words rather than as a capitalised flag at the end, and the
 * weighing rule now comes LAST. The old shape stated the rule first, as a parenthetical aside, and
 * closed on "The pick was INCORRECT." — a conclusion in the conclusion's position.
 */
export function snippetChoiceAttachment(choice) {
    const letter = (i) => OPTION_LETTERS[i] ?? String(i + 1);
    const picked = `option ${letter(choice.pickedIndex)} ("${choice.options[choice.pickedIndex] ?? ""}")`;
    // `correct` is the stored deterministic result; the index comparison is the same fact derived
    // from the indices. They disagree only on a hand-built counterfactual (the quality harness's
    // verdict-only control arm), and there the stored flag is the variable under test, so it wins.
    const fact = choice.correct
        ? `The candidate chose ${picked}, and that was the best answer.`
        : `The candidate chose ${picked}. The best answer was option ${letter(choice.correctIndex)}, ` +
            "whose wording is deliberately withheld from you: the choice is already recorded and put in " +
            "front of the human reviewer on its own, so working out what the candidate missed and " +
            "marking their explanation down for it would count one mistake twice.";
    return ("Machine-verified snippet choice — checked deterministically outside this conversation, and " +
        "settled fact. Do not re-derive it, dispute it, or repeat it back as a finding of your own.\n" +
        fact +
        "\nHow it bears on the score: it does not choose the level of the rubric — the spoken explanation does. Judge " +
        "that explanation on what it establishes: whether it engages with the artefact itself rather " +
        "than with what the artefact appears to be, whether its conclusion follows from the reasoning " +
        "the candidate actually showed, and what they said they would check next. The SAME explanation " +
        "earns the SAME level whether the choice it arrived at turned out right or wrong, and a right " +
        "choice carried by a vague, hollow or lucky explanation still scores low. The recorded result " +
        "may move the score within that level; it must never move it across one.");
}
// ── Override scopes and precedence ────────────────────────────────────────────
//
// Backing store: model_prompt_overrides (migration 0055). One row per
// (prompt_key, org_id, vertical_key); NULL means "any". The four scopes a row can occupy are the
// four combinations of those two nullable columns, and their precedence is just how many of the
// two are pinned — which is why `specificity` is a generated column in the table and a pure
// function here. The DB and this file must agree, so they compute it the same way.
export const PROMPT_SCOPE_TYPES = ["platform", "vertical", "org", "org_vertical"];
export function promptScopeTypeOf(row) {
    if (row.orgId && row.verticalKey)
        return "org_vertical";
    if (row.orgId)
        return "org";
    if (row.verticalKey)
        return "vertical";
    return "platform";
}
/**
 * How specific a row is: an org pin outranks a vertical pin, so the ordering is
 * org+vertical (3) > org (2) > vertical (1) > platform (0). Mirrors the generated column
 * `specificity` in migration 0055 — change one and you must change the other.
 */
export function promptScopeSpecificity(row) {
    return (row.orgId ? 2 : 0) + (row.verticalKey ? 1 : 0);
}
export const PROMPT_SCOPE_LABEL = {
    platform: "Platform",
    vertical: "Vertical",
    org: "Organisation",
    org_vertical: "Organisation + vertical",
};
/**
 * THE PRECEDENCE. One pure function, so the resolver that feeds the model and the Owner console's
 * "what actually runs here" preview give the same answer — a second implementation would
 * eventually disagree with the first, and the console would then be confidently wrong about what
 * a candidate was asked.
 *
 * Rows that do not apply to `scope` are discarded here as well as in SQL. The WHERE clause is the
 * fast path; this is the one that has to be right, because it is the one under test.
 */
export function pickPromptOverride(rows, scope) {
    const orgId = scope.orgId ?? null;
    const verticalKey = scope.verticalKey ?? null;
    let best = null;
    let bestScore = -1;
    for (const row of rows) {
        if (row.orgId !== null && row.orgId !== orgId)
            continue;
        if (row.verticalKey !== null && row.verticalKey !== verticalKey)
            continue;
        if (typeof row.promptText !== "string" || row.promptText.trim().length === 0)
            continue;
        const score = promptScopeSpecificity(row);
        // Strictly greater: the unique index makes an exact-specificity tie impossible for one prompt
        // key, and if a bad backfill ever produced one, keeping the FIRST row makes the winner depend
        // on the query's row order. Ordered SQL plus this rule means the same row wins every time.
        if (score > bestScore) {
            best = row;
            bestScore = score;
        }
    }
    return best;
}
//# sourceMappingURL=modelPrompts.js.map